/**
 * The catalogue feed: search plus continuous scroll, in one place.
 *
 * Two of Mitali's complaints about the old vendor app land here, and the rebuild
 * had made both worse. "We can see only 50 skus at once and it's very annoying to
 * not have a continuous scroll" - the new app showed 24 and had a button. "Search
 * functionalities are flawed" - the new app had no search at all. And the button
 * had a new bug the old app did not: `setCursor(next)` re-ran the fetch and
 * *replaced* the page, so tapping "Show more" discarded the 24 books already on
 * screen and there was no way back to one you liked.
 *
 * So this hook owns three things the previous code did not:
 *
 *   1. **Pages accumulate.** `items` only ever grows within a query. A page that
 *      arrives is concatenated, never assigned.
 *   2. **A late answer for an old query is dropped, not rendered.** Every request
 *      carries a sequence number and the query it was issued for; typing
 *      "dinosaur" fires several requests and the one that comes back last is not
 *      necessarily the one for what is now in the box. Rendering that is exactly
 *      what "search is flawed" feels like.
 *   3. **Changing the query resets the accumulator**, because appending results
 *      for "dinosaur" underneath results for "dino" is not a longer list, it is a
 *      wrong one.
 *
 * Deliberately not a `useResource`: that hook refetches on dependency change and
 * replaces its data, which is the correct model for a screen showing one thing
 * and the wrong one for a feed.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { endpoints, type PublicListing } from '../data/endpoints'

export type FeedStatus = 'loading' | 'error' | 'empty' | 'ready'

export interface CatalogueFeed {
  items: PublicListing[]
  status: FeedStatus
  errorCode: string | null
  /** True while a further page loads under items already on screen. */
  loadingMore: boolean
  /** Null when the feed has reached the end - the UI then says so rather than offering more. */
  nextCursor: string | null
  /** Total matches for a search, so the answer can be "18 books" before scrolling. */
  matched: number | null
  q: string
  setQ: (q: string) => void
  loadMore: () => void
  retry: () => void
}

/** How long to wait after the last keystroke. Short enough to feel live. */
export const SEARCH_DEBOUNCE_MS = 180

/** Below this, a query is a prefix of an intention, not a query. */
export const MIN_QUERY_LENGTH = 2

export function useCatalogueFeed(): CatalogueFeed {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<PublicListing[]>([])
  const [status, setStatus] = useState<FeedStatus>('loading')
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [matched, setMatched] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(0)

  // The sequence guard. Incremented on every issued request; a response whose
  // sequence is not the current one is discarded before it can touch state.
  const seq = useRef(0)
  const activeQuery = useRef('')

  const run = useCallback(async (query: string, cursor: string | null) => {
    const mine = ++seq.current
    const searching = query.trim().length >= MIN_QUERY_LENGTH
    activeQuery.current = query
    if (cursor === null) {
      setStatus('loading')
      setErrorCode(null)
    } else {
      setLoadingMore(true)
    }
    try {
      const page = searching
        ? await endpoints.search(query.trim(), { cursor })
        : await endpoints.browse({ cursor })
      if (mine !== seq.current) return
      const incoming = page.items
      setItems((prev) => (cursor === null ? incoming : [...prev, ...incoming]))
      setNextCursor(page.nextCursor)
      setMatched(searching ? ((page as { matched?: number }).matched ?? incoming.length) : null)
      // Emptiness is judged on the accumulated feed, not on the page: page two of
      // a search returning nothing is the end of the results, not "no results".
      setStatus(cursor === null && incoming.length === 0 ? 'empty' : 'ready')
    } catch (e) {
      if (mine !== seq.current) return
      const code = (e as { code?: string }).code ?? 'NETWORK'
      setErrorCode(code)
      // A failed *further* page keeps the books already on screen. Blanking a
      // feed because page three timed out is the worse of the two failures.
      setStatus(cursor === null ? 'error' : 'ready')
    } finally {
      if (mine === seq.current) setLoadingMore(false)
    }
  }, [])

  // Debounced first page. `attempt` is in the dependency list so a retry re-runs
  // the identical query - without it, retrying the same string is a no-op.
  useEffect(() => {
    const trimmed = q.trim()
    if (trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH) return
    const t = setTimeout(() => void run(q, null), q === '' ? 0 : SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q, attempt, run])

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return
    void run(activeQuery.current, nextCursor)
  }, [nextCursor, loadingMore, run])

  return {
    items,
    status,
    errorCode,
    loadingMore,
    nextCursor,
    matched,
    q,
    setQ,
    loadMore,
    retry: () => setAttempt((n) => n + 1),
  }
}
